import styled from '@emotion/styled';
import { Box, InputBase, MenuItem, MenuList, Paper, Popper as _Popper } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Clear from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { ChangeEvent, createElement, Dispatch, FocusEvent, FormEvent, SetStateAction, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SPACING_REGULAR } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';

type Props = {
  scrollToTop: () => void;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  setAudioSearchText: Dispatch<SetStateAction<string>>;
  useApi: boolean;
  searchAsset: (query: string) => void;
  recommendSearchTexts: ReadonlyArray<string>;
};

export const SearchForm = ({
  scrollToTop,
  searchText,
  setSearchText,
  setAudioSearchText,
  useApi,
  searchAsset,
  recommendSearchTexts,
}: Props) => {
  const [isOpenSearchWord, setIsOpenSearchWord] = useState(false);
  const [searchTextEl, setSearchTextEl] = useState<HTMLInputElement | null>(null);

  const search = (query: string) => {
    setIsOpenSearchWord(false);
    scrollToTop();

    if (useApi) {
      searchAsset(query);
    } else {
      setAudioSearchText(query);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();
    search(searchText);
  };

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchText(e.currentTarget.value);
  };

  const handleFocusInput = (e: FocusEvent<HTMLInputElement>) => {
    setSearchTextEl(e.currentTarget);
    setIsOpenSearchWord(true);
  };

  const handleBlurInput = () => {
    setIsOpenSearchWord(false);
  };

  const handleClickRecommend = (text: string) => () => {
    setSearchText(text);
    search(text);
  };

  return (
    <Box marginY={1}>
      <Form component="form" onSubmit={handleSubmit}>
        <FormattedMessage id="search_file_uploader.search_form.placeholder">
          {(placeholder: string) => (
            <Input
              placeholder={placeholder}
              value={searchText}
              onChange={handleChangeInput}
              onFocus={handleFocusInput}
              onBlur={handleBlurInput}
            />
          )}
        </FormattedMessage>
        <IconWrapButton type="submit">
          <SearchIcon />
        </IconWrapButton>
      </Form>

      {recommendSearchTexts.length > 0 && (
        <Popper open={isOpenSearchWord} anchorEl={searchTextEl} disablePortal={true} placement="bottom-start">
          <Paper elevation={3}>
            <MenuList>
              <RecommendHeader>
                <FormattedMessage id="search_file_uploader.recommend_search_text" />
                <IconWrapButton size="small">
                  <Clear />
                </IconWrapButton>
              </RecommendHeader>
              {recommendSearchTexts.map((text) => (
                <RecommendItem key={text} onMouseDown={handleClickRecommend(text)}>
                  {text}
                </RecommendItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      )}
    </Box>
  );
};

const Form = styled(Paper)`
  margin-bottom: ${SPACING_REGULAR}px;
  border: 2px #dfdfdf solid;
  .MuiInputBase-root {
    ${mq('sp')} {
      width: 100%;
    }
  }
`;

const IconWrapButton = styled(IconButton)`
  ${mq('sp')} {
    position: absolute;
    top: 18px;
    right: 20px;
    padding: 0;
  }
`;

const Input = styled(InputBase)`
  margin-left: 10px;
`;

const RecommendHeader = styled(MenuItem)`
  font-size: 0.8rem;
  line-height: 1;
  color: #f4af2d;
  &:hover {
    background-color: transparent;
  }
`;

const RecommendItem = styled(MenuItem)`
  font-size: 0.8rem;
  line-height: 1;
  ${mq('sp')} {
    :not(:first-child) {
      min-height: 33px;
    }
  }
`;

const Popper = styled(_Popper)`
  z-index: 100;
  ${mq('sp')} {
    width: 220px;
  }
`;
